/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { z } from 'zod';
import { Paddock, SEED_MIX_TYPE, SeedMix, SeedProductLine } from '../types/SeedOrderFormType';

export const roundedTo = (total: number) => {
  return Math.round(total * 100) / 100;
};

export const allPaddocksHectares = (paddocks: Paddock[]): number => {
  const total = paddocks?.reduce((acc: number, paddock: Paddock): number => {
    const value = z.coerce.number().parse(paddock.hectares);
    return value + acc;
  }, 0);

  return roundedTo(total);
};

export const allSeedsCount = (seeds: SeedProductLine[]): number => {
  return seeds?.reduce((acc: number, seed: SeedProductLine): number => {
    if (seed.isMixingFee) return acc;
    return acc + 1;
  }, 0);
};

export const allSeedsKg = (seeds: SeedProductLine[]): number => {
  const total = seeds?.reduce((acc: number, seed: SeedProductLine): number => {
    const value = z.coerce.number().parse(seed.quantity);
    if (seed.isMixingFee) return acc;
    return value + acc;
  }, 0);

  return roundedTo(total);
};

export const seedWeightForPaddock = (seed: SeedProductLine, paddock: Paddock): number => {
  if (seed.isMixingFee) return 0;
  const sowingRate = z.coerce.number().parse(seed.sowingRate);
  const hectares = z.coerce.number().parse(paddock.hectares);
  return roundedTo(sowingRate * hectares);
};

export const seedWeightForPaddockIgnoreIsMixing = (
  seed: SeedProductLine,
  paddock: Paddock,
): number => {
  const sowingRate = z.coerce.number().parse(seed.sowingRate);
  const hectares = z.coerce.number().parse(paddock.hectares);
  return roundedTo(sowingRate * hectares);
};

export const allSeedWeightForPaddock = (seeds: SeedProductLine[], paddock: Paddock): number => {
  const total = seeds.reduce(
    (acc: number, seed: SeedProductLine): number => seedWeightForPaddock(seed, paddock) + acc,
    0,
  );

  return roundedTo(total);
};

export const totalPriceForPaddock = (seeds: SeedProductLine[], paddock: Paddock): number => {
  const total = seeds.reduce((acc: number, seed: SeedProductLine): number => {
    let price = 0;
    const quantity = seed.isMixingFee
      ? allSeedWeightForPaddock(seeds, paddock)
      : seedWeightForPaddock(seed, paddock);
    if (seed.editedPriceExcGst) {
      price = quantity * seed.editedPriceExcGst;
    } else if (seed.discountedPriceExcGst) {
      price = (quantity * seed.discountedPriceExcGst) / seed.quantity;
    }
    return price + acc;
  }, 0);

  return total / paddock.hectares;
};

export const hasPremixInOrder = (mixes: SeedMix[]): boolean => {
  let hasPremix = false;
  mixes.forEach((mix) => {
    if (mix.mixType === SEED_MIX_TYPE.PREMIX) {
      hasPremix = true;
    }
  });
  return hasPremix;
};

export const calculateQuantity = (seedMixes: SeedMix[], mixIndex: number, productIndex: number) => {
  const totalHectares = allPaddocksHectares(seedMixes[mixIndex].paddocks);
  const sowingRate = seedMixes[mixIndex].products[productIndex].sowingRate;
  const quantity = totalHectares * sowingRate;
  const roundedUp = quantity % 1 !== 0;
  return { quantity: Math.ceil(quantity), roundedUp };
};

export const calculateSowingRate = (
  seedMixes: SeedMix[],
  mixIndex: number,
  productIndex: number,
) => {
  const totalHectares = allPaddocksHectares(seedMixes[mixIndex].paddocks);
  const quantity = seedMixes[mixIndex].products[productIndex].quantity;
  return roundedTo(quantity / totalHectares);
};

export const adjustQuantityAfterHectares = (seedMix: SeedMix): SeedMix => {
  const totalHectares = allPaddocksHectares(seedMix.paddocks);

  if (totalHectares === 0) {
    return seedMix;
  }

  const updatedProducts = [
    ...seedMix.products
      .filter((a) => !a.isMixingFee)
      .map((product) => {
        const quantity = roundedTo(product.sowingRate * totalHectares);
        const roundedUp = quantity % 1 !== 0;
        const updatedQuantity = Math.ceil(quantity);
        return {
          ...product,
          quantity: updatedQuantity,
          quantityRoundedUp: roundedUp,
        };
      }),
    ...seedMix.products.filter((a) => a.isMixingFee),
  ];

  const mixingFee = updatedProducts.find((a) => a.isMixingFee);
  if (mixingFee) {
    const newQuantity = updatedProducts
      .filter((a) => !a.isMixingFee)
      .reduce((acc, product) => acc + product.quantity, 0);

    // Find the index of the mixing fee product in the array
    const mixingFeeIndex = updatedProducts.findIndex((a) => a.isMixingFee);

    // Update the quantity of the mixing fee product
    updatedProducts[mixingFeeIndex] = {
      ...mixingFee, // Spread the existing properties of the mixing fee product
      quantity: newQuantity, // Update the quantity with the new value
    };
  }

  return {
    ...seedMix,
    products: updatedProducts,
  };
};

export const adjustSowingRateAfterQuantity = (seedMix: SeedMix): SeedMix => {
  const totalHectares = allPaddocksHectares(seedMix.paddocks);

  if (totalHectares === 0) {
    return seedMix;
  }

  const updatedProducts = [
    ...seedMix.products
      .filter((a) => !a.isMixingFee)
      .map((product) => {
        const updatedSowingRate = roundedTo(product.quantity / totalHectares);

        return {
          ...product,
          sowingRate: updatedSowingRate,
        };
      }),
    ...seedMix.products.filter((a) => a.isMixingFee),
  ];

  const mixingFee = updatedProducts.find((a) => a.isMixingFee);
  if (mixingFee) {
    const newQuantity = updatedProducts
      .filter((a) => !a.isMixingFee)
      .reduce((acc, product) => acc + product.quantity, 0);

    // Find the index of the mixing fee product in the array
    const mixingFeeIndex = updatedProducts.findIndex((a) => a.isMixingFee);

    // Update the quantity of the mixing fee product
    updatedProducts[mixingFeeIndex] = {
      ...mixingFee, // Spread the existing properties of the mixing fee product
      quantity: newQuantity, // Update the quantity with the new value
    };
  }

  return {
    ...seedMix,
    products: updatedProducts,
  };
};

export const adjustQuantityAfterSowingRate = (seedMix: SeedMix): SeedMix => {
  const totalHectares = allPaddocksHectares(seedMix.paddocks);

  if (totalHectares === 0) {
    return seedMix;
  }

  const updatedProducts = [
    ...seedMix.products
      .filter((a) => !a.isMixingFee)
      .map((product) => {
        const quantity = product.sowingRate * totalHectares;
        const roundedQuantity = Math.ceil(roundedTo(quantity));
        const roundedUp = roundedQuantity % 1 !== 0;

        return {
          ...product,
          quantity: roundedQuantity,
          quantityRoundedUp: roundedUp, // Set the quantityRoundedUp property if rounded up
        };
      }),
    ...seedMix.products.filter((a) => a.isMixingFee),
  ];

  const mixingFee = updatedProducts.find((a) => a.isMixingFee);
  if (mixingFee) {
    const newQuantity = updatedProducts
      .filter((a) => !a.isMixingFee)
      .reduce((acc, product) => acc + product.quantity, 0);

    // Find the index of the mixing fee product in the array
    const mixingFeeIndex = updatedProducts.findIndex((a) => a.isMixingFee);

    // Update the quantity of the mixing fee product
    updatedProducts[mixingFeeIndex] = {
      ...mixingFee, // Spread the existing properties of the mixing fee product
      quantity: newQuantity, // Update the quantity with the new value
    };
  }

  return {
    ...seedMix,
    products: updatedProducts,
  };
};
